import { calendarZone, localDay } from '../../shared/calendar';
import {
  type Appearance,
  type DesktopState,
  reminderHorizonMs,
  reminderLimit,
} from '../../shared/desktop';
import { type FocusSession, taskFocus } from '../../shared/focus';
import type { LocalRecord, Task } from '../../shared/model';
import { reminderSchedule } from '../../shared/reminder';

/** Reminders a little in the past are still published, so one due as the page republishes is kept. */
const publishedLateMs = 10 * 60 * 1000;

/**
 * What the page tells the desktop app: the running session for the menu bar's
 * clock, today's stones in Today's own counting, and the coming reminders by
 * the rule the phone and the server use. Pure, so it is unit-tested.
 */
export function desktopState(options: {
  signedIn: boolean;
  appearance: Appearance;
  dark: boolean;
  records: LocalRecord[];
  deviceId?: string;
  now?: Date;
  zone?: string;
}): DesktopState {
  const now = options.now ?? new Date(),
    zone = options.zone ?? calendarZone();

  if (!options.signedIn) {
    return {
      signedIn: false,
      appearance: options.appearance,
      dark: options.dark,
      focus: null,
      today: { placed: 0, total: 0 },
      reminders: [],
    };
  }

  const tasks = options.records.filter(r => r.kind === 'task' && !(r.value as Task).trashed);

  // The running session: this device's first, as the session panel chooses.
  const open = options.records.filter(
    r => r.kind === 'focus' && !r.deleted && (r.value as FocusSession).endedAt === null,
  );

  const session =
    open.find(r => (r.value as FocusSession).deviceId === options.deviceId) ?? open[0];

  const task = session && tasks.find(t => t.id === (session.value as FocusSession).taskId);

  const focus = task
    ? taskFocus(
        options.records,
        task.id,
        options.deviceId ?? (session.value as FocusSession).deviceId,
      )
    : undefined;

  // Today's commitments, counted as Today counts them.
  const day = localDay(now, zone);

  const commitments = tasks.filter(r => {
    const value = r.value as Task;

    return value.today && !value.dropped && (!value.done || value.completedOn === day);
  });

  const reminders = reminderSchedule(options.records, {
    from: new Date(now.getTime() - publishedLateMs),
    until: new Date(now.getTime() + reminderHorizonMs),
    zone,
  })
    .slice(0, reminderLimit)
    .map(reminder => ({
      key: reminder.key,
      recordId: reminder.recordId,
      channel: reminder.channel,
      fireAt: reminder.fireAt,
      title: reminder.title.slice(0, 500),
      body: reminder.body.slice(0, 1000),
    }));

  return {
    signedIn: true,
    appearance: options.appearance,
    dark: options.dark,
    focus:
      task && focus
        ? {
            taskId: task.id,
            title: (task.value as Task).title.slice(0, 500),
            runningSince: focus.runningSince,
            elapsedMs: Math.max(0, Math.round(focus.elapsedMs)),
          }
        : null,
    today: {
      placed: commitments.filter(r => (r.value as Task).done).length,
      total: commitments.length,
    },
    reminders,
  };
}
